import { DateTime } from 'luxon';
import { Options, RRule, Weekday } from 'rrule';
import { EventRepetition, WeeklyRepetition } from '../types';

/**
 * Adjusts the start date to the timezone.
 * E.g. if the start date is 2024-03-04T07:30:00.0000Z and the timezone is Europe/Berlin,
 * the function will return 2024-03-04T08:30:00.0000-01:00
 * (-1 hour because of the timezone offset that RRule adds, we need to remove, but still we need the timezone,
 * because RRule uses it to calculate the correct dates with DST changes; if we just did UTC, the DST changes would not be considered)
 */
export function adjustStartDateToTimezone(dtStart: Date, tzId: string) {
  // First, get offset value in that timezone for the given date using the Luxon library
  const { offset } = DateTime.fromJSDate(dtStart, { zone: tzId });

  const isoString = `${dtStart.toISOString().slice(0, 19)}${
    (offset / 60) * -1 < 0 ? '-' : '+'
  }${Math.abs(offset / 60)
    .toString()
    .padStart(2, '0')}:00`;

  return new Date(isoString);
}

function weekdayToRRuleWeekday(weekday: keyof WeeklyRepetition['days']) {
  const weekdayMap: {
    [key in keyof WeeklyRepetition['days']]: Weekday;
  } = {
    monday: Weekday.fromStr('MO'),
    tuesday: Weekday.fromStr('TU'),
    wednesday: Weekday.fromStr('WE'),
    thursday: Weekday.fromStr('TH'),
    friday: Weekday.fromStr('FR'),
    saturday: Weekday.fromStr('SA'),
    sunday: Weekday.fromStr('SU'),
  };

  return weekdayMap[weekday];
}

function getWeekday(date: Date): Weekday {
  const weekdayMap: {
    [key: number]: Weekday;
  } = {
    0: Weekday.fromStr('SU'),
    1: Weekday.fromStr('MO'),
    2: Weekday.fromStr('TU'),
    3: Weekday.fromStr('WE'),
    4: Weekday.fromStr('TH'),
    5: Weekday.fromStr('FR'),
    6: Weekday.fromStr('SA'),
  };

  return weekdayMap[date.getDay()];
}

type GetRRuleConfigOpts = {
  dtStart?: Date;
  tzId?: string;
};

export function getRRuleConfiguration(
  repetition: EventRepetition,
  { dtStart: dtStartParam, tzId }: GetRRuleConfigOpts = {}
) {
  const { interval, type, maxNumberOfRepetitions, repeatUntil } = repetition;

  const dtStart =
    dtStartParam && tzId ? adjustStartDateToTimezone(dtStartParam, tzId) : dtStartParam;

  const rruleOptions: Partial<Options> = {
    dtstart: dtStart,
    interval,
    until: repeatUntil,
    count: maxNumberOfRepetitions,
    wkst: repetition.type === 'weekly' && dtStart ? getWeekday(dtStart) : undefined,
    tzid: tzId,
  };

  switch (type) {
    case 'daily':
      {
        rruleOptions.freq = RRule.DAILY;
      }
      break;

    case 'weekly':
      {
        const { days } = repetition;

        rruleOptions.freq = RRule.WEEKLY;

        const weekdays = Object.entries(days)
          .filter(([, isRepeating]) => isRepeating)
          .map(([weekday]) => weekdayToRRuleWeekday(weekday as keyof WeeklyRepetition['days']));

        rruleOptions.byweekday = weekdays;
      }
      break;
    case 'monthly':
      {
        const { onDayOfMonth } = repetition;

        rruleOptions.freq = RRule.MONTHLY;

        rruleOptions.bymonthday = onDayOfMonth;
      }
      break;
    case 'yearly':
      {
        const { everyMonth, onDayOfMonth } = repetition;

        rruleOptions.freq = RRule.YEARLY;

        rruleOptions.bymonth = everyMonth;

        rruleOptions.bymonthday = onDayOfMonth;
      }
      break;
  }

  return rruleOptions;
}
